import {isEmpty, isEmptyArray, isNullOrUndefined} from './common'
import {useSitesStore} from '@/stores/sites'
import isValidDomain from 'is-valid-domain'
import validator from 'validator'
// 👉 Required Validator
export const requiredValidator = (value: any) => {
  if (isNullOrUndefined(value) || isEmptyArray(value) || value === false)
    return 'This field is required'

  return !!String(value).trim().length || 'This field is required'
}

export const requiredBooleanValidator = (value: any) => {
  if (isNullOrUndefined(value) || isEmptyArray(value))
    return 'This field is required'

  return value ?? 'This field is required'
}

export const minNumberValueValidator =
  (minValue: string | number) => (value: number | string) => {
    if (isNullOrUndefined(value)) return 'This field is required'
    return (
      Number(value) > Number(minValue) ||
      `This field must be greater than ${minValue}`
    )
  }

export const numberValidator = (value: number | string) => {
  return (
    (!isNaN(parseInt(value?.toString())) && !Number.isNaN(Number(value))) ||
    'This field must be a number'
  )
}

// 👉 Email Validator
export const emailValidator = (value: string | string[]) => {
  if (isEmpty(value)) return true
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  if (Array.isArray(value))
    return (
      value.every((val) => re.test(String(val))) ||
      'Please enter a valid email address'
    )

  return re.test(String(value)) || 'Please enter a valid email address'
}

// 👉 Password Validator
export const passwordValidator = (password: string) => {
  const regExp = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]){8,}/
  const validPassword = regExp.test(password)

  return (
    // eslint-disable-next-line operator-linebreak
    validPassword ||
    'Field must contain at least one uppercase, lowercase, minimum one digit and should be at least 8 characters long'
  )
}

// 👉 Confirm Password Validator
export const confirmedValidator = (value: any, target: any) =>
  value === target || 'Passwords do not match'

// 👉 Between Validator
export const betweenValidator = (value: any, min: any, max: any) => {
  const valueAsNumber = Number(value)

  return (
    (Number(min) <= valueAsNumber && Number(max) >= valueAsNumber) ||
    `Enter number between ${min} and ${max}`
  )
}

// 👉 Integer Validator
export const integerValidator = (value: any) => {
  if (isEmpty(value)) return true
  if (Array.isArray(value))
    return (
      value.every((val) => /^-?[0-9]+$/.test(String(val))) ||
      'This field must be an integer'
    )

  return /^-?[0-9]+$/.test(String(value)) || 'This field must be an integer'
}

// 👉 Regex Validator
export const regexValidator = (value: any, regex: any): string | boolean => {
  if (isEmpty(value)) return true
  let regeX = regex
  if (typeof regeX === 'string') regeX = new RegExp(regeX)
  if (Array.isArray(value))
    return value.every((val) => regexValidator(val, regeX))

  return regeX.test(String(value)) || 'The Regex field format is invalid'
}

// 👉 Alpha Validator
export const alphaValidator = (value: any) => {
  if (isEmpty(value)) return true

  return (
    /^[A-Z]*$/i.test(String(value)) ||
    'The Alpha field may only contain alphabetic characters'
  )
}
// 👉 URL Validator
export const urlValidator = (value: any) => {
  if (isEmpty(value)) return true
  return (
    validator.isURL(value, {
      protocols: ['http', 'https'],
    }) || 'URL is invalid'
  )
}

// 👉 Length Validator
export const lengthValidator = (length: number) => (value: any) => {
  if (isEmpty(value)) return true

  return (
    validator.isLength(value, {
      min: Number(length),
    }) || `The field must be at least ${length} characters`
  )
}
// 👉 Length Validator
export const minLengthValidator = (length: number) => (value: any) => {
  if (isEmpty(value)) return true

  return (
    validator.isLength(value, {
      min: Number(length),
    }) || `The field must be at least ${length} characters`
  )
}

// 👉 Max Length Validator
export const maxLengthValidator = (length: number) => (value: any) => {
  if (isEmpty(value)) return true

  return (
    validator.isLength(value, {
      max: Number(length),
    }) || `The field can have a maximum of ${length} characters`
  )
}

export const matchPasswordsValidator = (password: string) => (value: any) => {
  if (isEmpty(value)) return true

  return validator.equals(value, password) || 'Passwords do not match'
}

// 👉 Alpha-dash Validator
export const alphaDashValidator = (value: any) => {
  if (isEmpty(value)) return true
  const valueAsString = String(value)

  return /^[0-9A-Z_-]*$/i.test(valueAsString) || 'All Character are not valid'
}

export const strongPasswordValidator = (value: any) => {
  if (isEmpty(value)) return true
  const valueAsString = String(value)

  return (
    validator.isStrongPassword(valueAsString, {
      minLength: 8,
      minUppercase: 1,
      minNumbers: 1,
      minSymbols: 0,
    }) ||
    'Password must contain at least 8 characters, including one uppercase letter and one number'
  )
}
export const domainValidator = (value: any) => {
  if (validator.isEmpty(value)) return true
  if (!validator.isLength(value, { min: 5, max: 40 }))
    return 'Domain must be between 5 and 40 characters'

  if (validator.contains(value, '.'))
    return 'Not a valid domain. No dots allowed'
  if (!validator.isLowercase(value)) return 'Domain must be in lowercase'
  const valueAsString = String(value)
  return /^[a-z0-9-]*$/i.test(valueAsString) || 'Not a valid domain'
}
export const simpleDomainValidator = (value: any) => {
  if (validator.isEmpty(value)) return true
  if (!validator.isLowercase(value)) return 'Domain must be in lowercase'
  const valueAsString = String(value)
  if (value.endsWith('.')) return 'Domain cannot end with a dot'
  if (!isValidDomain(valueAsString)) return 'Not a valid domain'
}

export const domainAlreadyExist = (domain: string) => {
  const sitesStore = useSitesStore()
  const domains = sitesStore.selectedSiteDomains
  if (isEmpty(domains)) return true
  return (
    domains?.every((item: any) => item.domain !== domain) ||
    'Domain already exist in this site'
  )
}

export const maxImageSize = (files: File[], allowedSize: number) => {
  if (isEmpty(files)) return true
  const file = files[0]
  return file.size <= allowedSize
}

export const domainMultiSelectValidator = (isAll: boolean) => (value: any) => {
  if (isAll) return true
  if (isEmptyArray(value)) return 'Please select at least one domain'
  return true
}

export const optionalPhoneNumberValidator = (value: any) => {
  if (isEmpty(value)) return true
  return (
    /^\+\d{1,3}-?\d{3}-?\d{3}-?\d{4}$/.test(value) ||
    'Phone number is invalid. Please enter a valid phone number (e.g. +1-234-567-5890 or +12345675890)'
  )
}
